import { Box, CardActions, Divider, Drawer, Typography } from '@mui/material'
import { Button, ErrorMessage, IconButton } from 'components'
import {
  useLoadOrderProduct,
  useLoadOrderProductPrice,
  useLoadOrderProductTaxes
} from 'modules/orders/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdKeyboardArrowLeft } from 'react-icons/md'
import { ItemFormFields } from './itemFormFields'
import { useItemFormDrawerStore } from './itemFormDrawerStore'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  getAllowDecimalQuantity,
  numberToQuantityString,
  quantityToNumber,
  stringToNumber,
  useDebounce,
  uuId,
  Yup
} from 'utils'
import { axios } from 'lib'
import { ItemTotal } from './itemTotal'
import { useOrderStore } from 'modules/orders/store'
import { ItemDetails } from './itemDetails'
import { ItemConfigurationDrawer } from '../ItemConfiguration'
import BigNumber from 'bignumber.js'
import { generateNextSequence } from '../ItemConfiguration/helpers'

type Props = {
  unityId?: string
  customerId?: string
  invoiceTypeId?: string
}

const DRAWER_WIDTH = 530

const itemSchema = Yup.object({
  quantity: Yup.string().test('quantity', `Deve ser no mínimo 1`, (value) => {
    return Number(stringToNumber(value)) > 0
  }),
  quantityUn: Yup.string().test(
    'quantityUn',
    `Deve ser no mínimo 1`,
    (value) => {
      return Number(stringToNumber(value)) > 0
    }
  ),
  salePrice: Yup.string().test(
    'salePrice',
    'Deve ser maior que zero',
    (value) => {
      return Number(value) > 0
    }
  ),
  discountPercent: Yup.string().test(
    'discountPercent',
    'Valor Inválido',
    (value) => {
      return Number(value) >= 0
    }
  ),
  discount: Yup.string().test('discount', 'Valor Inválido', (value) => {
    return Number(value) >= 0
  }),
  pcpObservation: Yup.string().max(215),
  xPed: Yup.string().max(15),
  nItemPed: Yup.string().max(6)
})

const defaultValues = {
  priceTable: null,
  salePrice: '',
  defaultPrice: '',
  defaultPrice4: '',
  currentPrice: '',
  saleType: '12',
  quantity: '1',
  quantityUn: '',
  discountPercent: '',
  discount: '',
  pcpObservation: '',
  origin: false
}

export const ItemDrawer = (props: Props) => {
  const { unityId, customerId, invoiceTypeId } = props
  const [configurationId, setConfigurationId] = useState('')
  const [configurationDescription, setConfigurationDescription] = useState('')
  const [configurationFormValues, setConfigurationFormValues] = useState<any>(
    {}
  )
  const [uuid, setUuid] = useState('')
  const [isConfDrawerOpen, setIsConfDrawerOpen] = useState(false)

  const isDrawerOpen = useItemFormDrawerStore.useIsDrawerOpen()
  const setIsDrawerOpen = useItemFormDrawerStore.useSetIsDrawerOpen()
  const productId = useItemFormDrawerStore.useProductId()
  const selectedItem = useItemFormDrawerStore.useSelectedItem()
  const resetSelectedItem = useItemFormDrawerStore.useResetSelectedItem()
  const itemMode = useItemFormDrawerStore.useItemMode()

  const form = useOrderStore.useForm()
  const setForm = useOrderStore.useSetForm()
  const nextSequence = useOrderStore.useNextSequence()
  const sequence = useOrderStore.useSequence()
  const addProductForm = useOrderStore.useAddProductForm()
  const setSequence = useOrderStore.useSetSequence()
  const setIsTaxesRecalculePending =
    useOrderStore.useSetIsTaxesRecalculePending()

  const { watch } = useFormContext()

  const productMethods = useForm<any>({
    mode: 'all',
    resolver: yupResolver(itemSchema),
    reValidateMode: 'onChange',
    defaultValues
  })

  const [productSalePrice, quantity] = productMethods.watch([
    'salePrice',
    'quantity'
  ])

  const quantityDeb = useDebounce(quantityToNumber(quantity), 500)

  const cdPedidoReserva = watch('cdPedidoReserva')
  const parentOrderType = watch('orderType')
  const isTransfer = parentOrderType?.id === 'T'

  const {
    data: productRes,
    isLoading: productLoading,
    isFetching: productFetching
  } = useLoadOrderProduct({
    productId: productId,
    customerId: customerId || '',
    invoiceTypeId: invoiceTypeId || '',
    unityId: unityId || '',
    cdPedidoReserva:
      typeof cdPedidoReserva === 'string'
        ? cdPedidoReserva
        : cdPedidoReserva?.orderId ||
          cdPedidoReserva?.id ||
          cdPedidoReserva?.proposalId ||
          undefined
  })

  const hasConfiguration = productRes?.data?.product?.configuration?.id

  const {
    data: productPriceRes,
    isLoading: productPriceLoading,
    isFetching: productPriceFetching
  } = useLoadOrderProductPrice({
    orderId: form?.orderId || '',
    sequence: sequence[uuid],
    customerId: watch('customer')?.id,
    sellerId: watch('seller')?.id,
    saleTypeId: watch('orderType')?.id,
    paymentTermId: watch('paymentTerm')?.id,
    origin: Number(!!productMethods.watch('origin')),
    productId: productId,
    configurationId,
    // quantity: configurationFormValues?.quantity
    //   ? Number(configurationFormValues.quantity)
    //   : Number(quantityDeb),
    quantity: 1,
    enabled:
      productRes &&
      (!hasConfiguration || !!(!!hasConfiguration && !!configurationId)),
    unityId: unityId || ''
  })

  const hasRuleId = !!productPriceRes?.data?.ruleId

  const currentSaleType = productMethods.watch('saleType')

  const { data: taxes } = useLoadOrderProductTaxes({
    productId,
    items: form.orderItems,
    sequence: sequence[uuid],
    salePrice: Number(productSalePrice),
    quantity: Number(quantityDeb),
    icmsPercent:
      currentSaleType === '4'
        ? 4
        : productPriceRes?.data?.icmsPercent || selectedItem?.icmsPercent || 0,
    ipiPercent:
      productPriceRes?.data?.ipiPercent || selectedItem?.ipiPercent || 0,
    stPercent: productPriceRes?.data?.stPercent || selectedItem?.stPercent || 0,
    freightValue: watch('freightPrice') || 0
  })

  const formRef: any = useRef(null)

  const productIsLoading = productLoading || productFetching
  const productIsPriceLoading = productPriceLoading || productPriceFetching
  const isLoading = productIsLoading || productIsPriceLoading

  const handleBack = () => {
    setIsDrawerOpen(false)
  }

  const onSubmit = async (data: any) => {
    const confId = configurationId || selectedItem.configurationId
    const confDesc =
      configurationDescription || selectedItem.configurationDescription
    const confValues = Object.keys(configurationFormValues)?.length
      ? configurationFormValues
      : selectedItem?.configurationFormValues

    addProductForm({
      ...selectedItem,
      ...taxes,
      ...productRes?.data,
      ...productPriceRes?.data,
      ...data,
      uuid,
      origin: data.saleType === '4' ? productPriceRes?.data?.origin ?? 0 : 0,
      icmsPercent:
        data.saleType === '4'
          ? 4
          : productPriceRes?.data?.icmsPercent ??
            selectedItem?.icmsPercent ??
            0,
      sequence: sequence[uuid],
      configurationId: confId,
      configurationFormValues: confValues,
      configurationDescription: confDesc,
      // Preserva a flag isMaterial se existir
      isMaterial: selectedItem?.isMaterial || false
    })
    setIsTaxesRecalculePending(true)
    setIsDrawerOpen(false)
    resetSelectedItem()
  }

  const confDefaultValues = useMemo(() => {
    const df: any = {}
    productRes?.data?.product?.configuration?.particulars?.forEach((item) => {
      if (item.contentType === 'P' || item.contentType === 'L')
        df[item.id] = null

      if (item.contentType === 'F') df[item.id] = ''
    })
    return df
  }, [productRes?.data?.product?.configuration?.particulars])

  useEffect(() => {
    const d = productPriceRes?.data?.defaultPrice || 0
    const d4 = productPriceRes?.data?.defaultPrice4 || 0

    if (itemMode !== 'edit') {
      if (productPriceRes?.data?.orderId) {
        setForm({ orderId: productPriceRes?.data?.orderId })
      }
    }

    productMethods.setValue('defaultPrice', d)
    productMethods.setValue('defaultPrice4', d4)

    if (d4 <= 0) {
      productMethods.setValue('saleType', '12')
    }

    const saleType = productMethods.getValues('saleType')
    const basePrice = saleType === '4' && d4 > 0 ? d4 : d

    productMethods.setValue('currentPrice', basePrice)

    if (itemMode !== 'edit' && !productSalePrice) {
      productMethods.setValue('salePrice', basePrice)
    }
  }, [productPriceRes, productMethods, productSalePrice, setForm, itemMode])

  useEffect(() => {
    if (productRes?.data?.product?.configuration?.id && itemMode !== 'edit')
      setIsConfDrawerOpen(true)
  }, [productRes, setIsConfDrawerOpen, itemMode])

  useEffect(() => {
    if (itemMode === 'edit') {
      productMethods.setValue('defaultPrice', selectedItem.defaultPrice)
      productMethods.setValue('defaultPrice4', selectedItem.defaultPrice4)
      productMethods.setValue(
        'saleType',
        selectedItem.saleType === '4' ? '4' : '12'
      )
      productMethods.setValue(
        'currentPrice',
        selectedItem.currentPrice || selectedItem.defaultPrice
      )
    }
  }, [
    itemMode,
    productMethods,
    selectedItem.defaultPrice,
    selectedItem.defaultPrice4,
    selectedItem.saleType,
    selectedItem.currentPrice
  ])

  // useEffect(() => {
  //   productMethods.setValue('quantity', configurationFormValues?.quantity || 1)
  // }, [configurationFormValues, productMethods])

  useEffect(() => {
    const salePrice = selectedItem.salePrice
    const currentPrice = selectedItem.currentPrice || selectedItem.defaultPrice

    let discountVl: any = BigNumber(currentPrice || 0).minus(salePrice)

    let discountPerc: any = BigNumber(1)
      .minus(BigNumber(salePrice).div(currentPrice || 0))
      .times(100)

    discountVl =
      discountVl.toNumber() > 0 ? discountVl.toNumber().toFixed(2) : 0

    discountPerc =
      discountPerc.toNumber() > 0 ? discountPerc.toNumber().toFixed(2) : 0

    productMethods.setValue('salePrice', selectedItem.salePrice)
    productMethods.setValue(
      'quantity',
      numberToQuantityString(selectedItem.quantity)
    )
    productMethods.setValue(
      'quantityUn',
      numberToQuantityString(selectedItem.quantityUn)
    )
    productMethods.setValue('discount', discountVl)
    productMethods.setValue('discountPercent', discountPerc)
    productMethods.setValue('pcpObservation', selectedItem.pcpObservation)
    productMethods.setValue('origin', selectedItem.origin)
    setConfigurationId(selectedItem?.configurationId || '')
  }, [selectedItem, productMethods])

  useEffect(() => {
    if (productRes && hasConfiguration && configurationId) {
      const uuid = selectedItem?.uuid || uuId()
      nextSequence(uuid)
      setUuid(uuid)
    }
    if (productRes && !hasConfiguration) {
      const uuid = selectedItem?.uuid || uuId()
      nextSequence(uuid)
      setUuid(uuid)
    }
  }, [
    productId,
    configurationId,
    nextSequence,
    productRes,
    hasConfiguration,
    selectedItem?.uuid
  ])
  return (
    <>
      {isConfDrawerOpen && productRes?.data && (
        <ItemConfigurationDrawer
          product={{ ...productRes.data.product, ...selectedItem }}
          open={isConfDrawerOpen}
          onClose={() => setIsConfDrawerOpen(false)}
          onSuccess={async (data: any) => {
            if (itemMode === 'duplicate') {
              const uuid = uuId()
              setUuid(uuid)

              const itemSequence = generateNextSequence(uuid, sequence)
              setSequence(itemSequence)

              let quantity = selectedItem?.quantityUn

              const fields = data.formValues
              const productId =
                selectedItem?.product?.id || productRes?.data?.product?.id

              if (productId && !!Object.keys(fields || {}).length) {
                try {
                  const res: any = await axios.post(
                    '/orders/products/resolve-quantity',
                    {
                      productId,
                      configurationFormValues: fields,
                      quantityUn:
                        Number(stringToNumber(selectedItem?.quantityUn)) || 0
                    }
                  )
                  const qty = res?.data?.quantity
                  if (qty != null) {
                    quantity = numberToQuantityString(Number(qty))
                  }
                } catch (err) {
                  console.log(err)
                }
              }

              const p = {
                ...selectedItem,
                uuid,
                quantity,
                sequence: itemSequence[uuid],
                configurationId: data.configurationId,
                configurationDescription: data.configurationDescription,
                configurationFormValues: data.formValues
              }
              addProductForm(p)

              setIsDrawerOpen(false)
              resetSelectedItem()
            }
            setConfigurationFormValues(data.formValues)
            setConfigurationId(data.configurationId)
            setConfigurationDescription(data.configurationDescription)
          }}
          formValues={selectedItem.configurationFormValues}
          defaultValues={confDefaultValues}
        />
      )}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        variant="temporary"
      >
        <Box
          sx={{
            width: { xs: '100%', sm: DRAWER_WIDTH },
            height: '100vh',
            backgroundColor: 'white',
            paddingBottom: 16
          }}
        >
          <div
            style={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              padding: 12,
              zIndex: 97,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <IconButton onClick={() => setIsDrawerOpen(false)}>
                <MdKeyboardArrowLeft
                  size={24}
                  color="#565656"
                  style={{ background: '' }}
                />
              </IconButton>
              <div>
                <Typography
                  variant="subtitle1"
                  style={{
                    marginLeft: '1rem'
                  }}
                >
                  {productRes?.data?.product?.name || 'Sem nome'}
                </Typography>

                <Typography
                  variant="caption"
                  style={{
                    marginLeft: '1rem'
                  }}
                >
                  {productRes?.data?.product?.id} -{' '}
                  {productRes?.data?.product?.reference}
                </Typography>
              </div>
            </div>
            <Divider />
          </div>

          <FormProvider {...productMethods}>
            <div style={{ padding: 12 }}>
              <ItemFormFields
                ref={formRef}
                loadingPrice={productPriceFetching || productPriceLoading}
                isProductLoading={productLoading || productFetching}
                canBeImported={productRes?.data?.canBeImported}
                canBeImportedMessage={productRes?.data?.canBeImportedMessage}
                isTransfer={isTransfer}
                allowDecimalQuantity={getAllowDecimalQuantity(productRes?.data)}
                configurationFormValues={
                  Object.keys(configurationFormValues).length
                    ? configurationFormValues
                    : selectedItem?.configurationFormValues
                }
                item={{
                  ...productRes?.data
                }}
              />
            </div>
          </FormProvider>
          <div style={{ padding: 12, paddingBottom: 160 }}>
            <ItemDetails
              item={{
                ...selectedItem,
                ...productRes?.data,
                ...productPriceRes?.data,
                ...taxes,
                ...productMethods.watch(),
                icmsPercent:
                  productMethods.watch('saleType') === '4'
                    ? 4
                    : productPriceRes?.data?.icmsPercent ??
                      selectedItem?.icmsPercent ??
                      0,
                configurationId
              }}
              sequence={sequence[uuid]}
            />
          </div>

          <div
            style={{
              position: 'fixed',
              width: DRAWER_WIDTH,
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 99
            }}
          >
            <Divider />
            <div style={{ padding: 12 }}>
              <ItemTotal
                formValues={productMethods.watch()}
                priceValues={
                  productPriceRes?.data
                    ? { ...productPriceRes.data, ...taxes }
                    : { ...selectedItem, ...taxes }
                }
              />
            </div>
            <Divider />
            {!hasRuleId && (
              <ErrorMessage>
                Item não possui regra fiscal para esse tipo de venda, verifique
                o cadastro do cliente e em caso de dúvidas acione o setor
                fiscal!
              </ErrorMessage>
            )}
            <CardActions
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: 16
              }}
            >
              <Button type="button" variant="text" onClick={handleBack}>
                Voltar
              </Button>
              <Button
                onClick={productMethods.handleSubmit(onSubmit)}
                disabled={
                  isLoading || (!hasRuleId && !selectedItem?.isMaterial)
                }
              >
                Confirmar
              </Button>
            </CardActions>
          </div>
        </Box>
      </Drawer>
    </>
  )
}
